
/**
 * @fileoverview Product Variant Manager
 * Handles variant selection, URL updates, and custom events
 * 
 * 📡 EVENT SUMMARY:
 * 
 * EVENTS LISTENED:
 * - change (variant select) → Handles variant selection changes
 * - DOMContentLoaded → Initializes variant detection from URL
 * 
 * EVENTS DISPATCHED:
 * - variant:change → Emitted when variant selection changes
 * - variant:loaded → Emitted when variant is loaded from URL
 * - variant:unavailable → Emitted when selected variant is unavailable
 * 
 * EVENT DATA STRUCTURE:
 * - variant:change: { variantId: number, variant: object, isAvailable: boolean, previousVariantId: number }
 * - variant:loaded: { variantId: number, variant: object, isAvailable: boolean }
 * - variant:unavailable: { variantId: number, variant: object }
 */

import { Debug, Format } from '../cart/utils/index.js';

export class VariantManager {
  constructor(options = {}) {
    this.options = {
      variantSelector: '[data-variant-select]',
      variantInput: '[data-variant-id]',
      priceSelector: '[data-product-price]',
      comparePriceSelector: '[data-compare-at-price]:not(.variant-swatch)',
      addToCartButton: '[data-submit-button]',
      buttonTextSelector: '[data-button-text]',
      swatchSelector: '.variant-swatch',
      urlParameter: 'variant',
      ...options
    };
    
    this.currentVariantId = null;
    this.previousVariantId = null;
    this.variants = [];
    this.initialized = false;
    
    // Bind methods
    this.handleVariantChange = this.handleVariantChange.bind(this);
    this.updateURL = this.updateURL.bind(this);
    this.detectVariantFromURL = this.detectVariantFromURL.bind(this);
    this.updateUI = this.updateUI.bind(this);
    
    this.init();
  }

  /**
   * Initialize the variant manager
   */
  init() {
    console.log('🚀 [VariantManager] Starting initialization...');
    
    if (this.initialized) {
      console.log('⚠️ [VariantManager] Already initialized');
      return;
    }

    console.log('🔍 [VariantManager] Initializing variant manager');
    
    // Cache elements
    this.cacheElements();
    
    // Load variants data
    this.loadVariantsData();
    
    // Detect initial variant from URL first
    const urlVariantId = this.detectVariantFromURL();
    
    // If no URL variant, check what's currently selected in the form
    if (!urlVariantId) {
      const currentVariantId = this.getCurrentFormVariantId();
      if (currentVariantId) {
        console.log('📝 [VariantManager] No URL variant, using form selection:', currentVariantId);
        this.setVariant(currentVariantId, false);
      } else {
        console.log('🎯 [VariantManager] No URL variant and no form selection, using first available variant');
        // Only set first variant if nothing is selected
        const firstAvailableVariant = this.variants.find(v => v.available);
        if (firstAvailableVariant) {
          this.setVariant(firstAvailableVariant.id, false);
        }
      }
    }
    
    // Bind events
    this.bindEvents();
    
    this.initialized = true;
    console.log('✅ [VariantManager] Initialized successfully');
    
    // Expose globally for other systems to access
    window.variantManager = this;
    
    // Debug: Log final state after initialization
    this.debugVariantState('Initialization Complete');
  }

  /**
   * Cache DOM elements
   */
  cacheElements() {
    console.log('🔍 [VariantManager] Caching DOM elements...');
    
    this.elements = {
      variantSelect: document.querySelector(this.options.variantSelector),
      variantInput: document.querySelector(this.options.variantInput),
      priceElement: document.querySelector(this.options.priceSelector),
      comparePriceElement: document.querySelector(this.options.comparePriceSelector),
      addToCartButton: document.querySelector(this.options.addToCartButton),
      buttonTextElement: document.querySelector(this.options.buttonTextSelector),
      swatches: document.querySelectorAll(this.options.swatchSelector)
    };

    // Debug: Check for multiple compare price elements
    const allComparePriceElements = document.querySelectorAll(this.options.comparePriceSelector);
    console.log('🔍 [VariantManager] Compare price elements found:', {
      selector: this.options.comparePriceSelector,
      totalFound: allComparePriceElements.length,
      elements: Array.from(allComparePriceElements).map((el, index) => ({
        index,
        text: el.textContent,
        classes: el.className,
        hasHidden: el.classList.contains('hidden')
      }))
    });

    console.log('📋 [VariantManager] Elements cached:', {
      hasVariantSelect: !!this.elements.variantSelect,
      hasVariantInput: !!this.elements.variantInput,
      hasPriceElement: !!this.elements.priceElement,
      hasComparePriceElement: !!this.elements.comparePriceElement,
      hasAddToCartButton: !!this.elements.addToCartButton,
      swatchCount: this.elements.swatches.length,
      selectors: {
        variantSelector: this.options.variantSelector,
        variantInput: this.options.variantInput,
        priceSelector: this.options.priceSelector,
        comparePriceSelector: this.options.comparePriceSelector,
        swatchSelector: this.options.swatchSelector
      }
    });
  }

  /**
   * Load variants data from the DOM
   */
  loadVariantsData() {
    if (!this.elements.variantSelect) {
      Debug.log('warn', '[VariantManager] No variant select found');
      return;
    }

    this.variants = Array.from(this.elements.variantSelect.options).map(option => ({
      id: parseInt(option.value),
      title: option.textContent.split(' - ')[0].trim(),
      price: option.getAttribute('data-price'),
      compareAtPrice: option.getAttribute('data-compare-at-price'),
      available: !option.disabled,
      option: option
    }));

    Debug.log('info', '[VariantManager] Loaded variants data', {
      variantCount: this.variants.length,
      variants: this.variants.map(v => ({ 
        id: v.id, 
        title: v.title, 
        available: v.available,
        price: v.price,
        compareAtPrice: v.compareAtPrice
      })),
      currentSelectValue: this.elements.variantSelect.value,
      currentInputValue: this.elements.variantInput?.value
    });
  }

  /**
   * Detect variant from URL parameter
   */
  detectVariantFromURL() {
    const urlParams = new URLSearchParams(window.location.search);
    const variantParam = urlParams.get(this.options.urlParameter);
    
    if (!variantParam) {
      Debug.log('info', '[VariantManager] No variant parameter in URL');
      return null;
    }

    const variantId = parseInt(variantParam);
    if (isNaN(variantId)) {
      Debug.log('warn', '[VariantManager] Invalid variant ID in URL', { variantParam });
      return null;
    }

    Debug.log('info', '[VariantManager] Detected variant from URL', { variantId });
    
    // Find the variant
    const variant = this.variants.find(v => v.id === variantId);
    if (!variant) {
      Debug.log('warn', '[VariantManager] Variant not found', { variantId });
      return null;
    }

    // Check if this is different from what's currently selected in the form
    const currentFormVariantId = this.getCurrentFormVariantId();
    if (currentFormVariantId && currentFormVariantId !== variantId) {
      Debug.log('info', '[VariantManager] URL variant differs from form selection', {
        urlVariantId: variantId,
        formVariantId: currentFormVariantId
      });
    }

    // Set the variant
    this.setVariant(variantId, false); // Don't update URL since we're loading from it
    
    // Emit loaded event
    this.emitVariantEvent('variant:loaded', {
      variantId,
      variant,
      isAvailable: variant.available
    });
    
    return variantId;
  }

  /**
   * Bind event listeners
   */
  bindEvents() {
    // Bind select change event
    if (this.elements.variantSelect) {
      this.elements.variantSelect.addEventListener('change', this.handleVariantChange);
      Debug.log('info', '[VariantManager] Bound variant select change event');
    }
    
    // Bind swatch click events
    if (this.elements.swatches.length > 0) {
      console.log('🎨 [VariantManager] Binding swatch events for', this.elements.swatches.length, 'swatches');
      this.elements.swatches.forEach(swatch => {
        swatch.addEventListener('click', this.handleSwatchClick.bind(this));
      });
    }
  }

  /**
   * Handle variant selection change
   */
  handleVariantChange(event) {
    const select = event.target;
    const variantId = parseInt(select.value);
    
    if (isNaN(variantId)) {
      Debug.log('warn', '[VariantManager] Invalid variant ID selected', { value: select.value });
      return;
    }

    Debug.log('info', '[VariantManager] Variant selection changed by user', { 
      variantId,
      previousVariantId: this.currentVariantId,
      selectValue: select.value,
      selectText: select.options[select.selectedIndex]?.textContent
    });
    
    this.setVariant(variantId, true); // Update URL when user changes selection
  }

  updateVariantLabel(optionName, optionValue) {
    const targetLabel = document.querySelector(`[data-selected-option="${optionName}"]`)
    if (targetLabel != undefined) {
      targetLabel.innerHTML = optionValue;
    }
    
  }
  /**
   * Handle swatch click
   */
  handleSwatchClick(event) {
    const swatch = event.target;
    const variantId = parseInt(swatch.dataset.variantId);
    
    if (isNaN(variantId)) {
      Debug.log('warn', '[VariantManager] Invalid variant ID in swatch', { 
        variantId: swatch.dataset.variantId,
        swatch: swatch.textContent
      });
      return;
    }

    // Don't allow selection of unavailable variants
    if (swatch.disabled) {
      Debug.log('warn', '[VariantManager] Attempted to select unavailable variant', { variantId });
      return;
    }

    Debug.log('info', '[VariantManager] Swatch clicked by user', { 
      variantId,
      previousVariantId: this.currentVariantId,
      swatchText: swatch.textContent,
      optionName: swatch.dataset.optionName,
      optionValue: swatch.dataset.optionValue
    });
    
    this.setVariant(variantId, true, swatch.dataset.optionName, swatch.getAttribute('aria-label').split(':').pop().trim()); // Update URL when user changes selection
  }

  /**
   * Set the current variant
   */
  setVariant(variantId, updateURL = true, optionName, optionValue) {
    const variant = this.variants.find(v => v.id === variantId);
    if (!variant) {
      Debug.log('warn', '[VariantManager] Variant not found', { variantId });
      return;
    }

    console.log("Updating variant")
    this.previousVariantId = this.currentVariantId;
    this.currentVariantId = variantId;

    Debug.log('info', '[VariantManager] Setting variant', {
      variantId,
      previousVariantId: this.previousVariantId,
      variant: { 
        id: variant.id, 
        title: variant.title, 
        available: variant.available,
        price: variant.price,
        compareAtPrice: variant.compareAtPrice
      },
      updateURL
    });

    // Update form inputs
    this.updateFormInputs(variantId);
    
    // Update UI
    this.updateUI(variant);
    
    // Update URL if requested
    if (updateURL) {
      this.updateURL(variantId);
    }

    // Update texts
    // console.log("Update text")
    document.querySelectorAll('[data-variant-title]').forEach((title) => {
      // console.log(title)
      title.innerHTML = variant.title;
    })

    // Emit change event
    this.emitVariantEvent('variant:change', {
      variantId,
      variant,
      isAvailable: variant.available,
      previousVariantId: this.previousVariantId
    });

    // Emit unavailable event if variant is not available
    if (!variant.available) {
      this.emitVariantEvent('variant:unavailable', {
        variantId,
        variant
      });
    }
    
    // Debug: Log variant state after setting
    this.debugVariantState('Variant Set');
    if(optionName !=  undefined) {
      this.updateVariantLabel(optionName, optionValue)
    }
  }

  /**
   * Update form inputs with new variant ID
   */
  updateFormInputs(variantId) {
    if (this.elements.variantInput) {
      this.elements.variantInput.value = variantId;
    }
    
    if (this.elements.variantSelect) {
      this.elements.variantSelect.value = variantId;
    }
  }

  /**
   * Update UI elements with variant data
   */
  updateUI(variant) {
    // Update price
    if (this.elements.priceElement && variant.price) {
      const formattedPrice = Format.money(parseInt(variant.price));
      this.elements.priceElement.textContent = formattedPrice;
    }

    // Update compare-at price
    if (this.elements.comparePriceElement) {
      console.log('🔍 [VariantManager] Compare price update:', {
        hasComparePriceElement: !!this.elements.comparePriceElement,
        variantCompareAtPrice: variant.compareAtPrice,
        elementText: this.elements.comparePriceElement.textContent,
        hasHiddenClass: this.elements.comparePriceElement.classList.contains('hidden'),
        elementClasses: this.elements.comparePriceElement.className,
        elementStyle: this.elements.comparePriceElement.style.display
      });
      
      if (variant.compareAtPrice) {
        const formattedComparePrice = Format.money(parseInt(variant.compareAtPrice));
        this.elements.comparePriceElement.textContent = formattedComparePrice;
        this.elements.comparePriceElement.classList.remove('hidden');
        this.elements.comparePriceElement.style.display = 'block';
        console.log('✅ [VariantManager] Showed compare price:', formattedComparePrice);
        console.log('🔍 [VariantManager] After removing hidden class:', {
          hasHiddenClass: this.elements.comparePriceElement.classList.contains('hidden'),
          elementClasses: this.elements.comparePriceElement.className,
          elementStyle: this.elements.comparePriceElement.style.display,
          computedStyle: window.getComputedStyle(this.elements.comparePriceElement).display
        });
      } else {
        // Hide compare price element if no compare-at-price
        this.elements.comparePriceElement.classList.add('hidden');
        this.elements.comparePriceElement.style.display = 'none';
        console.log('❌ [VariantManager] Hid compare price element');
      }
    } 

    // Update add to cart button
    if (this.elements.addToCartButton) {
      this.elements.addToCartButton.disabled = !variant.available;
      
      if (this.elements.buttonTextElement) {
        const form = this.elements.addToCartButton.closest('form');
        const wrapper = this.elements.addToCartButton.closest('[data-custom-button-text]');
        const customText = (form?.dataset?.customButtonText || wrapper?.dataset?.customButtonText)?.trim();
        const label = variant.available ? (customText || 'Add to Cart') : 'Sold Out';
        this.elements.buttonTextElement.textContent = label;
      }
    }

    // Update swatch states
    this.updateSwatchStates(variant);

    Debug.log('info', '[VariantManager] UI updated', {
      variantId: variant.id,
      price: variant.price,
      formattedPrice: variant.price ? Format.money(parseInt(variant.price)) : null,
      compareAtPrice: variant.compareAtPrice,
      formattedComparePrice: variant.compareAtPrice ? Format.money(parseInt(variant.compareAtPrice)) : null,
      available: variant.available
    });
  }

  /**
   * Update swatch visual states
   */
  updateSwatchStates(variant) {
    if (!this.elements.swatches.length) return;

    this.elements.swatches.forEach(swatch => {
      const swatchVariantId = parseInt(swatch.dataset.variantId);
      
      if (swatchVariantId === variant.id) {
        // This swatch represents the current variant
        swatch.classList.add('active');
        swatch.setAttribute('aria-pressed', 'true');
      } else {
        // This swatch represents a different variant
        swatch.classList.remove('active');
        swatch.setAttribute('aria-pressed', 'false');
      }
    });

    Debug.log('info', '[VariantManager] Swatch states updated', {
      variantId: variant.id,
      swatchCount: this.elements.swatches.length
    });
  }

  /**
   * Update URL with variant parameter
   */
  updateURL(variantId) {
    const url = new URL(window.location);
    
    if (variantId) {
      url.searchParams.set(this.options.urlParameter, variantId.toString());
    } else {
      url.searchParams.delete(this.options.urlParameter);
    }

    // Update URL without page reload
    window.history.replaceState({}, '', url.toString());
    
    Debug.log('info', '[VariantManager] URL updated', {
      variantId,
      newURL: url.toString()
    });
  }

  /**
   * Emit custom variant event
   */
  emitVariantEvent(eventName, detail) {
    const event = new CustomEvent(eventName, {
      detail,
      bubbles: true,
      cancelable: true
    });

    document.dispatchEvent(event);
    
    Debug.log('info', '[VariantManager] Emitted event', {
      eventName,
      detail
    });
  }

  /**
   * Get current variant
   */
  getCurrentVariant() {
    return this.variants.find(v => v.id === this.currentVariantId);
  }

  /**
   * Get current variant ID
   */
  getCurrentVariantId() {
    return this.currentVariantId;
  }

  /**
   * Check if current variant is available
   */
  isCurrentVariantAvailable() {
    const variant = this.getCurrentVariant();
    return variant ? variant.available : false;
  }

  /**
   * Get the currently selected variant ID from the form
   */
  getCurrentFormVariantId() {
    // Check hidden input first
    if (this.elements.variantInput && this.elements.variantInput.value) {
      const inputValue = parseInt(this.elements.variantInput.value);
      if (!isNaN(inputValue)) {
        return inputValue;
      }
    }
    
    // Check select element
    if (this.elements.variantSelect && this.elements.variantSelect.value) {
      const selectValue = parseInt(this.elements.variantSelect.value);
      if (!isNaN(selectValue)) {
        return selectValue;
      }
    }
    
    return null;
  }

  /**
   * Debug: Log current variant state
   * @param {string} context - Context for the debug log
   */
  debugVariantState(context) {
    const currentVariant = this.getCurrentVariant();
    const formVariantId = this.getCurrentFormVariantId();
    const urlParams = new URLSearchParams(window.location.search);
    const urlVariantParam = urlParams.get(this.options.urlParameter);
    
    console.log(`🔍 [VariantManager Debug] ${context}:`, {
      currentVariantId: this.currentVariantId,
      previousVariantId: this.previousVariantId,
      formVariantId,
      urlVariantParam,
      currentVariant: currentVariant ? {
        id: currentVariant.id,
        title: currentVariant.title,
        available: currentVariant.available,
        price: currentVariant.price,
        compareAtPrice: currentVariant.compareAtPrice
      } : null,
      formElements: {
        hasVariantSelect: !!this.elements.variantSelect,
        hasVariantInput: !!this.elements.variantInput,
        selectValue: this.elements.variantSelect?.value,
        inputValue: this.elements.variantInput?.value
      },
      totalVariants: this.variants.length,
      availableVariants: this.variants.filter(v => v.available).length
    });
  }

  /**
   * Destroy the variant manager
   */
  destroy() {
    if (this.elements.variantSelect) {
      this.elements.variantSelect.removeEventListener('change', this.handleVariantChange);
    }
    
    // Remove swatch event listeners
    if (this.elements.swatches.length > 0) {
      this.elements.swatches.forEach(swatch => {
        swatch.removeEventListener('click', this.handleSwatchClick);
      });
    }
    
    this.initialized = false;
    Debug.log('info', '[VariantManager] Destroyed');
  }
} 